import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { motion } from "framer-motion";
import { CSSProperties, useMemo } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { ItemRarity } from "@/domains/inventory/constants/itemRarity";
import { IS_DEV } from "@/domains/main/constants/other";
import accentColorByRarity from "@/domains/inventory/helpers/accentColorByRarity";
import useTooltip from "@/domains/inventory/hooks/useTooltip";
import InventoryBorderBox from "@/domains/inventory/components/InventoryBorderBox";
import InventoryTooltip from "@/domains/inventory/components/InventoryTooltip";

type Props = {
  inventoryId?: number;
  tradeId?: number;
  onContextMenu?: (e: React.MouseEvent, item: InventoryItem) => void;
  onMemoizedItem: (item: InventoryItem | null) => void;
  onDoubleClickItem?: (item: InventoryItem) => void | Promise<void>;
  item: InventoryItem;
  disabled?: boolean;
};

export default function InventoryGridItem({
  inventoryId,
  tradeId,
  onContextMenu,
  onMemoizedItem,
  onDoubleClickItem,
  item,
  disabled,
}: Props) {
  const accentColor = useMemo(
    () => accentColorByRarity[item.itemTemplate.rarity] || accentColorByRarity[ItemRarity.Common],
    [item.itemTemplate.rarity],
  );

  const width = useMemo(() => `calc(4.375rem * ${item.size.width} - 1px)`, [item.size.width]);
  const height = useMemo(() => `calc(4.375rem * ${item.size.height} - 1px)`, [item.size.height]);

  const { itemRef, tooltipPosition, tooltipRef, handleMouseLeave, handleMouseEnter, isHovered } =
    useTooltip();

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `item-${item.id}-${inventoryId ? `inv${inventoryId}` : ""}-${tradeId ? `trade${tradeId}` : ""}`,
    data: { ...item, payload: { width, height } },
    disabled,
  });

  const { setNodeRef: setDropRef } = useDroppable({
    id: `item-${item.id}-${item.id}-${inventoryId ? `inv${inventoryId}` : ""}-${tradeId ? `trade${tradeId}` : ""}`,
    data: { ...item },
  });

  const combinedRef = (node: HTMLDivElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  if (isDragging && !item.itemTemplate.isStackable) return null;

  return (
    <>
      <div
        ref={combinedRef}
        className={clsx(
          "absolute group hover:z-1 z-1",
          "touch-manipulation", // это очень важно
          {
            "grayscale opacity-40": disabled,
          },
        )}
        style={
          {
            gridColumnStart: item.position.x + 1,
            gridRowStart: item.position.y + 1,
            width,
            height,
            "--width": width,
            "--height": height,
          } as CSSProperties
        }
        onContextMenu={(e) => {
          if (!disabled) {
            onContextMenu?.(e, item);
          }
        }}
        onMouseOver={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={() => onMemoizedItem(item)}
        onDoubleClick={() => {
          if (!disabled) {
            onDoubleClickItem?.(item);
          }
        }}
        {...attributes}
        {...listeners}
      >
        <InventoryBorderBox
          ref={itemRef}
          color={accentColor}
          className="w-full h-full bg-dark-800 hover:brightness-110 cursor-pointer transition-all"
        >
          <div
            className={clsx(
              "relative w-full h-full flex flex-col gap-1 items-center justify-center",
            )}
          >
            <div
              className={clsx("w-full h-full flex items-center justify-center", {
                "-rotate-90 ": item.isRotated,
              })}
              style={{
                ...(item.isRotated
                  ? { width: "calc(0.8 * var(--height))", height: "calc(0.8 * var(--width))" }
                  : { width: "calc(0.8 * var(--width))", height: "calc(0.8 * var(--height))" }),
                backgroundSize: "90% 90%",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundImage: `url(https://cdn.region.game/images/inventory/items/${item.itemId}.webp)`,
              }}
            />
          </div>

          {IS_DEV && (
            <div className="absolute bottom-1 left-1 text-10 whitespace-nowrap">
              {item.itemTemplate.name}
            </div>
          )}

          {!!item.wear && (
            <div className="absolute top-1 left-1 leading-[0.75] h-5 px-1.5 flex items-center text-11 font-bold text-[#84FF97] rounded-[0.3125rem] bg-gradient-to-r from-[#84FF97]/10 to-[#84FF97]/5">
              {Number((item.wear * 100).toFixed(2))}%
            </div>
          )}

          {!!item.quantity && (item.itemTemplate.isStackable || item.itemTemplate.maxStack > 1) && (
            <div
              className="absolute flex items-center leading-[0.75] justify-center px-1 h-4 min-w-4 rounded-[0.3125rem] text-11 font-bold bottom-1 right-1"
              style={{ background: `${accentColor}33` }}
            >
              {item.quantity}
            </div>
          )}
        </InventoryBorderBox>
      </div>
      {isHovered &&
        !isDragging &&
        createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            ref={tooltipRef}
            className={clsx(
              "fixed z-[9999] h-fit w-fit",
              "bg-[#1A1A1A] p-3.5 rounded-[0.625rem] shadow-lg whitespace-nowrap",
              "transition-opacity delay-1000 duration-200",
              isHovered ? "opacity-100" : "opacity-0",
            )}
            style={{ ...tooltipPosition, boxShadow: "0px 10px 15px 0px #121212" }}
          >
            <InventoryTooltip {...item} />
          </motion.div>,
          document.body,
        )}
    </>
  );
}
